import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import { getProductInfo, selectItem, viewSearchResults } from '@/utils/analytics.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import useCategoryFilters from '@/hooks/useCategoryFilters.js';
import Link from 'next/link';
import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import Head from 'next/head';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import {
  BoldEnum,
  WaveBreadCrumb,
  HeadingEnum,
  SortingAndFilters,
  TextEnum,
  Heading,
  Text,
  ProductCatalogueV2,
  OffersSnackbar,
  SnackBar,
} from '@desquared/wave-components-library';
import FiltersFAB from '@/components/FiltersFAB/FiltersFAB';
import PageLayout from '@/components/PageLayout';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import FiltersAside from '@/components/FiltersAside/FiltersAside';
import EmptyState from '@/components/EmptyState/EmptyState';
import CategoriesMenu from '@/components/CategoriesMenu/CategoriesMenu';
import { productsForOrder } from '@/store/cart';
import { getSearchResultsV31 } from '@/api/Search';
import { getProductsWithMainCategory, sortByPosition } from '@/utils/category';
import { checkListStatus } from '@/utils/lists';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import {
  addProduct,
  removeProduct,
  getBundleOfferDescription,
  getProductOrderLimit,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  openListSideSheet,
} from '@/utils/products';
import { SEARCH_SORT_OPTIONS } from '@/constants/sort.js';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { getCategoriesFromFile } from '@/utils/getCategories';
import { trimmCategories } from '@/utils/trimmCategories';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { getLayoutProps } from '@/utils/layout/layout';
import { useTranslations } from 'next-intl';

import styles from '@/styles/Categories.module.css';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import DefaultProductImage from '../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { ModalContext } from '@/contexts/ModalProvider';
import { useIsRtl } from '@/contexts/DirectionContext.jsx';

//export const SORT_OPTIONS = ['default', 'default', 'alphabetical', 'lowFirst', 'highFirst', 'kiloPrice'];
export default function Search({ categoriesFromFile, initialProducts = [], responsiveLogo, emptyStates }) {
  const router = useRouter();
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const isMobile = useWindowUnderThreshold(850);
  const slug = [];
  const [limitations, setLimitations] = useState([]);

  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();

  const [selectedSort, setSelectedSort] = useState(router.query.sort || 0);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const [productsOrder, setProductsOrder] = useState(false);
  const t = useTranslations();
  const a = useTranslations('common.asides');
  const isRtl = useIsRtl();

  const observer = useRef(null);
  const { formatter } = useContext(CurrencyContext);
  const { openErrorModal, close } = useContext(ModalContext);
  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  useEffect(() => {
    viewSearchResults(router?.query?.product);
  }, []);

  const {
    isLoading: isFiltersLoading,
    selectedFilters,
    handleSelectFilter,
    numberOfSelectedFilters,
    clearFilters,
    handleApplyFilters,
    getFiltersFromUrl,
  } = useCategoryFilters(router?.query?.category, {
    allowAttributes: false, // maybe this should be !!router?.query?.category cause theoretically we could have attrs and stuff when we have a category
    allowBrands: false,
    allowCategoryKinds: false,
    allowTags: false,
  });

  const {
    data: searchResults,
    isLoading: searchResultsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: searchError,
  } = useInfiniteQuery({
    enabled: !!router.query.product,
    queryKey: ['search-results', router.query.product, getCollectionTypes(), router.locale, selectedSort, router.query],
    initialPageParam: 0,
    queryFn: ({ queryKey, pageParam }) => {
      const { offerFilters } = getFiltersFromUrl();
      const category = router.query.category;

      return getSearchResultsV31({
        term: queryKey[1],
        collectionTypes: getCollectionTypes()?.length ? getCollectionTypes() : ['Common'],
        pageNumber: pageParam.toString(),
        pageSize: 20,
        ...(category && { category }),
        sort: [SEARCH_SORT_OPTIONS[selectedSort]],
        ...offerFilters,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pageNumber + 1 >= lastPage.totalPages) return undefined;

      return lastPage.pageNumber + 1;
    },
    // Stale time of 15 minutes
    staleTime: 1000 * 60 * 15,
  });

  useEffect(() => {
    if (searchError) {
      openErrorModal({
        title: t('search.error_modal.title'),
        text: t('search.error_modal.text'),
        actions: [
          {
            btnLabel: t('search.error_modal.action'),
            onClick: () => {
              close();
            },
          },
        ],
        img: {
          url: emptyStates.default_warning.image,
          ariaLabel: emptyStates.default_warning.description,
          size: 'medium',
        },
      });
    }
  }, [searchError, openErrorModal, close, t]);

  const products = useMemo(() => {
    if (!searchResults) return [];

    setTotalCount(searchResults.pages[0].totalCount);
    return getProductsWithMainCategory(
      {
        data: searchResults.pages.flatMap(page => page.data.products).map(enhanceProductWithSlug),
      },
      categoriesFromFile,
      productTags,
      bundleOffersSummary,
      personalizedProductsIds,
      a
    ).map(p => {
      // Mapping to add quantity limit
      p = { ...p, setMaxSnackBar: setShowSnackbar };
      if (!limitations || isEmpty(limitations)) return p;

      return { ...p, quantityLimit: getProductOrderLimit(limitations, p) };
    });
  }, [
    searchResults,
    categoriesFromFile,
    productTags,
    bundleOffersSummary,
    setTotalCount,
    limitations,
    productsOrder,
    personalizedProductsIds,
  ]);

  useEffect(() => {
    if (!searchResults) return;

    setTotalCount(searchResults.pages[0].totalCount);
  }, [searchResults]);

  useEffect(() => {
    async function getLimitations() {
      const limitation = await getOrFetchOrderLimitations();

      setLimitations(limitation);
    }

    getLimitations();
  }, []);

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (searchResultsLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [searchResultsLoading, isFetchingNextPage, fetchNextPage]
  );

  const breadCrumbItems = [
    { title: t('search.title'), link: '#' },
    { title: router?.query?.product, link: '#' },
  ];

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);
    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
      productsForOrder$.unsubscribe();
    };
  }, []);
  const { normalCategories, featuredCategories } = trimmCategories(
    categoriesFromFile,
    t('common.categories_menu.all_categories')
  );
  const sortedNormalCategories = sortByPosition(normalCategories);
  const sortedFeaturedCategories = sortByPosition(featuredCategories);

  const allCategories = [...sortedFeaturedCategories, ...sortedNormalCategories];

  return (
    <>
      <Head>
        <title>{process.env.displayName}</title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} isRtl={isRtl} />
          </div>
        ) : null}

        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesContainer}>
            <div className={styles.categoriesInnerContainer}>
              <CategoriesMenu categories={allCategories} slug={slug} featuredAmmount={featuredCategories.length} />
            </div>
          </div>
          <div className={styles.slugProductsContainer}>
            {isMobile ? null : (
              <div className={styles.breadCrumbAndSortContainer}>
                <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} isRtl={isRtl} />
              </div>
            )}

            {isMobile ? (
              <>
                <ScrollToTop />

                <FiltersFAB
                  onClick={() => {
                    setIsFiltersSheetOpen(true);
                  }}
                />
              </>
            ) : null}
            <div className={styles.breadCrumbAndSortContainer}>
              <div className={styles.headings}>
                <Heading type={HeadingEnum.HeadingM} bold={BoldEnum.Bold}>
                  {t('search.title')}
                </Heading>
                <Text type={TextEnum.Assistive}>{t('search.total_count', { count: totalCount })}</Text>
              </div>

              {!isMobile && (
                <SortingAndFilters
                  text={t('category_details.sorting_filters.btn_title')}
                  numberOfFilters={numberOfSelectedFilters || undefined}
                  onClick={() => {
                    setIsFiltersSheetOpen(true);
                  }}
                />
              )}
            </div>

            <div className={styles.catalogueRow}>
              {searchResultsLoading || !router.query.product ? (
                <ProductCatalogueV2 isLoading products={new Array(1)} loaderTilesAmount={12} />
              ) : products.length ? (
                <>
                  <ProductCatalogueV2
                    title=""
                    products={products}
                    LinkComponent={props => {
                      const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                      return (
                        <Link
                          prefetch={false}
                          {...propsWithCleanUrl}
                          onClick={() => {
                            const product = getProductBasedOnSlug(products, propsWithCleanUrl.href);

                            if (product) {
                              selectItem(getProductInfo(product), {
                                item_list_id: 'search_list',
                                item_list_name: 'Search list',
                                index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                              });
                            }
                          }}
                        />
                      );
                    }}
                    bundleOffersSummary={bundleOffersSummary}
                    lastItemRef={lastResultItemRef}
                    addProduct={product =>
                      addProduct(
                        product,
                        false,
                        {
                          item_list_id: 'search_list',
                          item_list_name: 'search_list',
                        },
                        false,
                        t('common.asides.side_cart.no_comment')
                      )
                    }
                    removeProduct={product =>
                      removeProduct(
                        product,
                        {
                          item_list_id: 'search_list',
                          item_list_name: 'search_list',
                        },
                        t('common.asides.side_cart.no_comment')
                      )
                    }
                    defaultQsText={t('common.qs.default')}
                    defaultOutOfStockText={t('common.asides.out_of_stock')}
                    defaultQsUnavailableText={t('common.qs.default_unavailable')}
                    ImageComponent={CustomImage}
                    offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                    isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                    ctaIcon={process.env.ctaIcon}
                    listIcon={process.env.listIcon}
                    checkStatus={checkListStatus}
                    onListClick={openListSideSheet}
                    currencyFormatter={formatter}
                    hasRoundedCorners={process.env.hasRoundedCorners}
                    defaultProductImage={defaultProdImg}
                    forYouLabel={t('common.user.profile.menu.for_you')}
                  />
                  {isFetchingNextPage ? (
                    <ProductCatalogueV2 isLoading products={new Array(1)} loaderTilesAmount={5} />
                  ) : null}
                </>
              ) : (
                <EmptyState />
              )}

              {hasNextPage ? <div className={styles.footerSpacer}></div> : null}
            </div>
          </div>
        </div>
      </main>

      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('common.snackbar')} position={'center'} />

      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('common.offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        hasShadow={true}
      />

      {isFiltersSheetOpen && (
        <FiltersAside
          isOpen={isFiltersSheetOpen}
          isLoading={isFiltersLoading}
          handleApplyFilters={handleApplyFilters}
          clearFilters={clearFilters}
          handleClose={() => setIsFiltersSheetOpen(false)}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleSelectFilter}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          hasClosest
          hideOffers={process.env.hideOffers}
        />
      )}
    </>
  );
}

Search.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  const categoriesFromFile = await getCategoriesFromFile(context.locale);

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    category_details: (await import(`../../../prebuild-data/i18n/${context.locale}/category_details.json`)).default,
    search: (await import(`../../../prebuild-data/i18n/${context.locale}/search.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      categoriesFromFile,
      messages,
    },
  };
}
